import { GROUP_CHAT_PROFILE_UPDATED_NOTIFY, MSG_PROCESSING_STATUS_ID } from '../../Helpers/Chat/Constant';
import { isViewOnceMessage } from '../../Helpers/Chat/viewOnce';
import { readMetaData } from '../location/locationRules';
import { isPollMessage } from '../poll/pollRules';

export const FORWARD_LABEL = { NONE: 'none', ONCE: 'once', MANY: 'many' };

// Spellings the mobile apps accept too, so a label written by any client reads the same.
const COUNT_KEYS = ['forwardedcount', 'forwarded_count', 'forwardcount', 'forward_count', 'fwdcount', 'fwd_count'];
const FLAG_KEYS = ['isforwarded', 'is_forwarded', 'forwarded'];
const MANY_THRESHOLD = 2;

/**
 * Tells whether a message can be forwarded. Polls, view once media, call logs, notices,
 * deleted messages and messages the server has not stored yet cannot.
 *
 * @param {object} message Message object of the conversation.
 * @returns {boolean} Whether "Forward" is offered and the message can be selected for it.
 */
export const canForwardMessage = message => {
    if (!message || !message.msgId || message.deleteStatus) return false;
    if (Number(message.msgStatus) === MSG_PROCESSING_STATUS_ID) return false;
    if (isPollMessage(message) || isViewOnceMessage(message)) return false;
    const messageType = message.msgBody?.message_type;
    if (!messageType || messageType === GROUP_CHAT_PROFILE_UPDATED_NOTIFY) return false;
    return messageType !== 'call' && message.msgType !== 'call' && readMetaData(message.metaData).kind !== 'call';
};

/**
 * Reads the forward keys of a message's meta data.
 *
 * @param {object|string} metaData Message meta data, parsed or not.
 * @returns {{ isForwarded: boolean, count: number }} Count is 0 when absent.
 */
export const readForwardInfo = metaData => {
    const entries = Object.entries(readMetaData(metaData)).map(([key, value]) => [key.toLowerCase(), value]);
    const valueOf = keys => entries.find(([key]) => keys.includes(key))?.[1];
    const rawCount = String(valueOf(COUNT_KEYS) ?? '')
        .trim()
        .toLowerCase();
    const count = rawCount === 'many' ? MANY_THRESHOLD : Number.parseInt(rawCount, 10) || 0;
    const flag = String(valueOf(FLAG_KEYS) ?? '')
        .trim()
        .toLowerCase();
    return { isForwarded: flag === 'true' || flag === '1' || count > 0, count };
};

/**
 * Picks the forwarded label a message shows: many times from two chats, once otherwise.
 *
 * @param {object|string} metaData Message meta data, parsed or not.
 * @returns {string} One of FORWARD_LABEL.
 */
export const getForwardLabel = metaData => {
    const { isForwarded, count } = readForwardInfo(metaData);
    if (count >= MANY_THRESHOLD) return FORWARD_LABEL.MANY;
    return isForwarded ? FORWARD_LABEL.ONCE : FORWARD_LABEL.NONE;
};

export const isForwardedMany = metaData => getForwardLabel(metaData) === FORWARD_LABEL.MANY;

/**
 * Tells whether the user wrote the message: passing one's own words on is not a forward.
 *
 * @param {object} message Message object.
 * @param {string} userId Id of the signed-in user.
 * @returns {boolean} True for the user's message that carries no forward keys.
 */
export const isOwnOriginalMessage = (message, userId) => {
    const authorId = message?.publisherId || message?.fromUserId;
    return Boolean(userId) && authorId === userId && !readForwardInfo(message?.metaData).isForwarded;
};

/**
 * Builds the forward keys a copy carries. The count is the number of chats of this forward,
 * replacing the count of an already forwarded original.
 *
 * @param {object} message Message being forwarded.
 * @param {number} destinationCount Number of chats chosen.
 * @param {string} userId Id of the signed-in user.
 * @returns {object|null} `{ isForwarded, forwardedCount }`, or null for the user's own message.
 */
export const forwardStamp = (message, destinationCount, userId) => {
    if (isOwnOriginalMessage(message, userId)) return null;
    return { isForwarded: 'true', forwardedCount: String(Math.max(1, destinationCount)) };
};
